import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import VideoCard from '@/components/base/VideoCard';
import { videoCategories, featuredVideos, allVideos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蘑菇视频',
    url: 'https://www.mogu-video.com',
    description: '蘑菇视频是您最佳的在线视频平台，汇聚海量高清影视、短视频、综艺、纪录片等优质内容，支持免费在线观看与流畅播放。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蘑菇视频',
    url: 'https://www.mogu-video.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.mogu-video.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOMeta
        title="蘑菇视频 - 海量高清视频在线免费观看"
        description="蘑菇视频网站是您最佳的在线视频平台，汇聚海量高清影视、短视频、综艺、纪录片等优质内容，支持免费在线观看与流畅播放。无论您喜欢哪种视频类型，蘑菇视频网站都能满足您的需求。"
        keywords="蘑菇视频网站,蘑菇视频在线观看,蘑菇视频免费播放,蘑菇视频高清下载,蘑菇视频最新内容,蘑菇短视频平台,在线视频网站推荐"
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20Chinese%20video%20streaming%20platform%20logo%20with%20mushroom%20icon%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20minimalist%20design%2C%20cinematic%20atmosphere%2C%20clean%20composition&width=1200&height=630&seq=home-og-01&orientation=landscape"
        schema={[organizationSchema, websiteSchema]}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20interior%20with%20warm%20golden%20spotlights%2C%20red%20velvet%20curtains%2C%20abstract%20geometric%20shapes%2C%20atmospheric%20lighting%2C%20elegant%20modern%20entertainment%20venue%2C%20deep%20shadows%20and%20highlights%2C%20luxurious%20streaming%20platform%20branding&width=1600&height=900&seq=home-hero-01&orientation=landscape"
            alt="蘑菇视频 - 海量高清视频在线观看"
            title="蘑菇视频 - 您的在线视频平台"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="w-full max-w-[720px]">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-500 shadow-lg">
                  <i className="ri-play-circle-fill text-3xl md:text-4xl text-white"></i>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold font-heading text-white">
                  蘑菇视频
                </h1>
              </div>
              <p className="text-lg md:text-xl text-white/90 mb-3">
                海量高清视频在线免费观看平台
              </p>
              <p className="text-sm md:text-base text-white/70 max-w-[560px] mx-auto leading-relaxed">
                汇聚影视、综艺、动漫、纪录片等优质内容，随时随地畅享精彩视听体验
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <Link
                  to="/category/dianying"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  立即观看
                </Link>
                <Link
                  to="/about"
                  className="w-full sm:w-auto px-8 py-3 bg-white/15 hover:bg-white/25 text-white font-medium rounded-full backdrop-blur-sm transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary-500"></div>
            <div className="w-2 h-2 rounded-full bg-white/40"></div>
            <div className="w-2 h-2 rounded-full bg-white/40"></div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '10万+', label: '高清视频' },
                { value: '5000万+', label: '月活用户' },
                { value: '8大', label: '内容分类' },
                { value: '4K', label: '超清画质' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Categories */}
        <section id="video-categories" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-14">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              视频分类
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-[480px] mx-auto">
              八大内容分类，覆盖影视综艺动漫纪录片等热门领域，按需挑选想看的内容
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {videoCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-6 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <h3 className="mt-3 text-sm font-semibold text-foreground-800 group-hover:text-primary-700 transition-colors">
                  {cat.name}
                </h3>
                <p className="mt-1 text-xs text-foreground-400">
                  {cat.count.toLocaleString()} 部
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot / Featured Videos */}
        <section className="bg-background-100 py-14">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  热门推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  实时热门内容，大家都在看
                </p>
              </div>
              <Link
                to="/category/dianying"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5" data-product-shop="">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/category/dianying"
                className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Videos */}
        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                最新内容
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                每日更新，新鲜内容不断档
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {allVideos.slice(5, 13).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Platform Features */}
        <section className="bg-secondary-900 text-background-50 py-14">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading">
                为什么选择蘑菇视频
              </h2>
              <p className="mt-3 text-sm text-foreground-400">
                致力于为用户提供最优质的在线视频观看体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K超高清画质播放，画面细腻色彩饱满，带来影院级观影享受' },
                { icon: 'ri-speed-up-line', title: '极速加载', desc: '全球CDN加速节点覆盖，视频秒开不卡顿，畅享流畅观看体验' },
                { icon: 'ri-smartphone-line', title: '多端同步', desc: '支持手机、平板、电脑多终端观看，播放进度云端同步，随时随地接着看' },
                { icon: 'ri-shield-check-line', title: '正版内容', desc: '所有视频内容均经过正版授权，安全可靠，让您安心享受每一部作品' },
              ].map((feat) => (
                <div key={feat.title} className="text-center p-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/20 mx-auto">
                    <i className={`${feat.icon} text-2xl text-primary-400`}></i>
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{feat.title}</h3>
                  <p className="mt-2 text-sm text-foreground-400 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Article Highlights */}
        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                精选文章
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                影视资讯、行业观察、观影指南
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {articles.slice(0, 4).map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[4/3] bg-background-200">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - 蘑菇视频`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                      {article.tags[0]}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20curves%2C%20golden%20and%20amber%20tones%2C%20elegant%20modern%20design%2C%20minimalist%20streaming%20platform%20aesthetic%2C%20smooth%20textures&width=1600&height=500&seq=home-cta-01&orientation=landscape"
            alt=""
            className="w-full h-[280px] md:h-[360px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-white">
              立即开启您的观影之旅
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-[480px]">
              海量高清内容免费观看，随时随地畅享精彩
            </p>
            <Link
              to="/category/dianying"
              className="mt-6 inline-flex px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
            >
              开始观看
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}